<template>
  <div class="quorum-group-card" data-test-id="quorum-group-card">
    <div class="quorum-group-card__cards">
      <InviteeCard
        v-for="slot in slotSignees"
        :key="slot.signee.signeeId"
        :attachments-allowed="showsAttachment(slot.signee)"
        :participant="slot.signee"
        :slot-label="
          i18n.t('labels.slot_number', { number: slot.slotIndex + 1 })
        "
      />
    </div>

    <v-expansion-panels
      bg-color="transparent"
      class="quorum-group-card__accordion"
    >
      <v-expansion-panel
        bg-color="transparent"
        data-test-id="quorum-group-accordion"
      >
        <v-expansion-panel-title data-test-id="quorum-group-accordion-toggle">
          <span class="text-body-2 text-grey" :style="{ fontSize: '14px' }">{{
            summaryText
          }}</span>
          <template #actions="{ expanded }">
            <v-icon
              aria-hidden="true"
              class="text-grey"
              :class="{ 'fa-rotate-180': expanded }"
              size="14"
            >
              far fa-chevron-down
            </v-icon>
          </template>
        </v-expansion-panel-title>
        <v-expansion-panel-text data-test-id="quorum-group-members">
          <div
            v-for="signee in group"
            :key="signee.signeeId"
            class="quorum-group-card__member text-body-2 text-grey-darken-2 text-truncate"
          >
            {{ signee.email || i18n.t('texts.no_email_yet') }}
          </div>
        </v-expansion-panel-text>
      </v-expansion-panel>
    </v-expansion-panels>
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue';
import type { PropType } from 'vue';
import { useI18n } from 'vue-i18n';
import InviteeCard from '@/components/create/InviteeCard.vue';
import { useCreateStore } from '@/stores/create.ts';
import type { Signee } from '@/types/Signee.ts';
import { SigneeType } from '@/types/enums/SigneeType.ts';

const props = defineProps({
  group: {
    type: Array as PropType<Signee[]>,
    required: true
  },
  // Document-wide "signees can attach files" flag and the id of the one
  // signee allowed to show the paperclip affordance — passed through from the
  // parent so quorum cards stay consistent with the plain (non-quorum) grid.
  attachmentsAllowed: {
    type: Boolean,
    default: false
  },
  firstSigneeId: {
    type: String as PropType<string | null>,
    default: null
  }
});

const i18n = useI18n();
const createStore = useCreateStore();

const isApprover = computed(
  () => props.group[0]?.signeeType === SigneeType.APPROVE
);

const total = computed(() => props.group.length);
const quorum = computed(() => createStore.getGroupQuorum(props.group));

// The envelope signee group backing this sign-order group — the authoritative
// owner of the shared signature slots.
const signeeGroup = computed(() =>
  createStore.findSigneeGroupForGroup(props.group)
);

// Only the first `requiredSignatures` members own a shared slot — the rest
// are other potential signers who could fulfill the quorum, shown in the
// accordion below but without their own slot card.
//
// Slots are owned by the envelope signee group, NOT by `signeesOrdered`: both
// the group signings lookup (`getSigneeGroupSlotIndex`) and the PDF
// placeholders index into `signeeGroup.signees`. `props.group` comes from
// `signeesOrdered` and is additionally re-sorted (approvers first), so
// deriving the slot number from its array index made a card read another
// member's slot — reporting a placement the member never made. Enumerate the
// group members instead so card N is genuinely slot N.
const slotSignees = computed<Array<{ signee: Signee; slotIndex: number }>>(
  () => {
    const limit = quorum.value || total.value;
    const members = signeeGroup.value?.signees ?? [];
    // A group that isn't persisted yet (draft) has no envelope counterpart —
    // the rendered order is then the only thing available.
    if (members.length === 0) {
      return props.group
        .slice(0, limit)
        .map((signee, slotIndex) => ({ signee, slotIndex }));
    }
    const signeesById = new Map(
      props.group.map((signee) => [signee.signeeId, signee])
    );
    return members
      .map((member, slotIndex) => ({
        signee: signeesById.get(member.signeeId),
        slotIndex
      }))
      .filter(
        (entry): entry is { signee: Signee; slotIndex: number } =>
          entry.signee !== undefined && entry.slotIndex < limit
      );
  }
);

const summaryText = computed(() =>
  i18n.t(
    isApprover.value
      ? 'texts.quorum_need_to_approve'
      : 'texts.quorum_need_to_sign',
    { required: quorum.value, total: total.value },
    quorum.value ?? 1
  )
);

function showsAttachment(signee: Signee): boolean {
  return props.attachmentsAllowed && signee.signeeId === props.firstSigneeId;
}
</script>

<style scoped lang="scss">
.quorum-group-card {
  grid-column: 1 / -1;
  min-width: 0;
}

.quorum-group-card__cards {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(150px, 1fr));
  gap: 12px;
  margin-bottom: 12px;
}

.quorum-group-card__accordion :deep(.v-expansion-panel),
.quorum-group-card__accordion :deep(.v-expansion-panel-title),
.quorum-group-card__accordion :deep(.v-expansion-panel-text) {
  background-color: transparent !important;
}

.quorum-group-card__accordion :deep(.v-expansion-panel-title) {
  min-height: 24px !important;
  height: 24px;
  padding-top: 0;
  padding-bottom: 0;
}

.quorum-group-card__accordion :deep(.v-expansion-panel-title--active) {
  min-height: 24px !important;
}

.quorum-group-card__accordion :deep(.v-expansion-panel-text__wrapper) {
  padding-bottom: 0 !important;
}

.quorum-group-card__accordion
  :deep(.v-expansion-panel-title > .v-expansion-panel-title__overlay) {
  opacity: 0 !important;
}

.quorum-group-card__member {
  padding-left: 2px;
}

.quorum-group-card__member:not(:last-child) {
  margin-bottom: 8px;
}
</style>
